import {api} from "@/shared/api";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {FeatureModuleItem, FeatureModuleItemBuildOptions} from "./featureModuleItem";
import {formatDecimalNumber} from "@/shared/utils/datefunctions";
import {FeatureModule, FeatureModulePriceCalculationResult} from "@/shared/timr";
import {PricingChangeType} from "@/shared/timrTypes";

export interface FeatureModuleSelectionDialogConfig {
	baseFeatureModules: FeatureModule[];
	extensionFeatureModules: FeatureModule[];
	activeFeatureModules: FeatureModule[];
	allFeatureModulesInConfiguration?: FeatureModule[];
	oldPrice?: number;
	userCount?: number;
	allowedChange?: PricingChangeType;
}

export enum PaymentPeriod {
	MONTHLY = "MONTHLY",
	ANNUALLY = "ANNUALLY"
}

export abstract class FeatureModuleSelectionBase extends HTMLElement {

	protected static readonly DEFAULT_CURRENCY = "EUR";

	protected config: FeatureModuleSelectionDialogConfig;
	protected pricePerUser: number = 0;
	protected basePrice: number = 0;

	private billingCycleSelection: ButtonRadioGroup<PaymentPeriod>;

	protected calculatePricePerUserRequest: JQuery.jqXHR;
	protected calculateFeatureModulePriceRequest: JQuery.jqXHR;


	protected abstract connectedCallback(): void;

	protected buildFeatureModuleItems(config: FeatureModuleSelectionDialogConfig, buildOptions?: FeatureModuleItemBuildOptions): HTMLElement {
		const base = document.getElementById("baseFeatureModules");
		base.replaceChildren();
		const extensions = document.getElementById("extensionFeatureModules");
		extensions.replaceChildren();

		const activeBaseFeatureModules = this.setActiveFlagForFeatureModules(config.baseFeatureModules, config.activeFeatureModules, config.allFeatureModulesInConfiguration);
		for (const featureModule of activeBaseFeatureModules) {
			const featureModuleItem = document.createElement("feature-module-item") as FeatureModuleItem;
			featureModuleItem.featureModule = featureModule;
			if (buildOptions) {
				featureModuleItem.buildOptions = buildOptions;
			}
			featureModuleItem.addEventListener(FeatureModuleItem.EVENT_BASE_FEATURE_MODULE_SELECTED, e => {
				const customEvent = e as CustomEvent<{
					baseFeatureModule: FeatureModule,
					active: boolean
				}>;
				this.ifIndividualOnlyActiveDeselectAllOthers(customEvent.detail.baseFeatureModule, customEvent.detail.active);
				this.updateExtensionFeatureModuleSelection();
			});
			featureModuleItem.addEventListener(FeatureModuleItem.EVENT_FEATURE_MODULE_TOGGLED, () => this.onFeatureModuleSelected());
			base.append(featureModuleItem);
		}

		const availableExtensionFeatureModules = this.setActiveFlagForFeatureModules(config.extensionFeatureModules, config.activeFeatureModules, config.allFeatureModulesInConfiguration);
		for (const featureModule of availableExtensionFeatureModules) {
			const featureModuleItem = document.createElement("feature-module-item") as FeatureModuleItem;
			featureModuleItem.featureModule = featureModule;
			if (buildOptions) {
				featureModuleItem.buildOptions = buildOptions;
			}
			featureModuleItem.addEventListener(FeatureModuleItem.EVENT_FEATURE_MODULE_TOGGLED, () => this.onFeatureModuleSelected());
			extensions.append(featureModuleItem);
		}
		document.getElementById("extensionFeatureModulesHeader").style.display = availableExtensionFeatureModules.length > 0 ? "block" : "none";

		this.buildMonthlyYearlySwitch();
		this.buildModalFooter();

		this.updateExtensionFeatureModuleSelection();
		return document.getElementById("featureModuleSelectionDialog");
	}

	protected initMonthlyYearlySwitch(monthlyBtnSelector: string, yearlyBtnSelector: string): void {
		this.billingCycleSelection = ButtonRadioGroup.typed<PaymentPeriod>({
			value: PaymentPeriod.MONTHLY,
			button: document.querySelector(monthlyBtnSelector),
		}, {
			value: PaymentPeriod.ANNUALLY,
			button: document.querySelector(yearlyBtnSelector),
			selected: true
		}).onValueChanged((billingCycle: PaymentPeriod) => {
			this.onFeatureModuleSelected();
			this.updateFeatureModulePrice(billingCycle);
		});

		this.updateFeatureModulePrice(PaymentPeriod.ANNUALLY)
	}

	protected abstract buildMonthlyYearlySwitch(): void;

	protected abstract buildModalFooter(): void;

	protected updateFeatureModulePrice(billingCycle: PaymentPeriod): void {
		this.calculateFeatureModulePriceRequest?.abort();
		this.calculateFeatureModulePriceRequest = api("pricing/calculateFeatureModulePrice.json")
			.get({
				featureModuleIds: this.getFeatureModuleItems().map((fm) => fm.featureModule.id),
				selectedModuleIds: this.getSelectedModules().map((fm) => fm.id),
				billingCycle: billingCycle
			})
			.done(results => {
				results.forEach(result => {
					const featureModuleItem = this.getFeatureModuleItems().find((fmi) => fmi.featureModule.id === result.featureModuleId);
					if (featureModuleItem) {
						featureModuleItem.updatePrice(result.basePrice, result.pricePerUser);
						featureModuleItem.updateDiscountInfo(result.discountResult);
					}
				});
			});
	}

	private setActiveFlagForFeatureModules(availableFeatureModules: FeatureModule[], activeFeatureModules: FeatureModule[], allFeatureModulesInConfiguration: FeatureModule[]): FeatureModule[] {
		if (!availableFeatureModules) {
			return [];
		}
		availableFeatureModules.forEach(availableFeatureModule => {
			// Set every available feature module to active if it is in the updated state
			availableFeatureModule.active = activeFeatureModules.some((activeFeatureModule) => activeFeatureModule.id === availableFeatureModule.id);
			availableFeatureModule.trialUntil = allFeatureModulesInConfiguration?.find(fm => fm.id === availableFeatureModule.id)?.trialUntil;
			// Merge add-ons too
			this.setActiveFlagForFeatureModules(availableFeatureModule.addOns, activeFeatureModules, allFeatureModulesInConfiguration);
		});
		return availableFeatureModules;
	}

	private ifIndividualOnlyActiveDeselectAllOthers(baseFeatureModule: FeatureModule, active: boolean) {
		if (active && baseFeatureModule.individualOnly) {
			for (const featureModuleItem of document.querySelectorAll("#baseFeatureModules > feature-module-item")) {
				const item = featureModuleItem as FeatureModuleItem;
				if (item.featureModule.id !== baseFeatureModule.id) {
					item.setSwitcheryStateOnItem(false, true);
				}
			}
		} else {
			for (const featureModuleItem of document.querySelectorAll("#baseFeatureModules > feature-module-item")) {
				const item = featureModuleItem as FeatureModuleItem;
				if (item.isIndividualOnly()) {
					item.setSwitcheryStateOnItem(false, true);
				}
			}
		}
	}

	private updateExtensionFeatureModuleSelection() {
		let selectionAllowed = false;
		for (const featureModuleItem of document.getElementById("baseFeatureModules").children) {
			const item = featureModuleItem as FeatureModuleItem;
			if (item.isActive() && !item.isIndividualOnly()) {
				selectionAllowed = true;
				break;
			}
		}
		for (const featureModuleItem of document.getElementById("extensionFeatureModules").children) {
			const item = featureModuleItem as FeatureModuleItem;
			item.toggleSwitcheryOnItem(selectionAllowed);
		}
	}

	protected onFeatureModuleSelected() {
		this.updatePricePerUser(this.getSelectedModules());
		this.updateFeatureModulePrice(this.billingCycleSelection.getValue())
	}

	protected getSelectedModules(): FeatureModule[] {
		const selectedModules: FeatureModule[] = [];
		for (const featureModuleItem of this.querySelectorAll("#baseFeatureModules > feature-module-item, #extensionFeatureModules > feature-module-item")) {
			const item = featureModuleItem as FeatureModuleItem;
			selectedModules.push(...item.getSelectedModules());
		}
		return selectedModules;
	}

	protected getFeatureModuleItems(): FeatureModuleItem[] {
		const featureModuleItems: FeatureModuleItem[] = [];
		for (const featureModuleItem of this.querySelectorAll("feature-module-item")) {
			const item = featureModuleItem as FeatureModuleItem;
			featureModuleItems.push(item);
		}
		return featureModuleItems;
	}

	protected updatePricePerUser(selectedModules: FeatureModule[]) {
		this.calculatePricePerUserRequest?.abort();
		this.calculatePricePerUserRequest = api("pricing/calculatePricePerUser.json")
			.get({
				selectedModuleIds: selectedModules.map((fm) => fm.id),
				users: this.config.userCount || 1
			})
			.done((data: FeatureModulePriceCalculationResult) => {
				this.pricePerUser = data.pricePerUserMonthly;
				this.basePrice = data.basePriceMonthly;
				document.getElementById("totalPrice").innerHTML = this.formatPricePerUser(data.monthlyPrice, data.yearlyPrice);
				this.toggleSaveButton(selectedModules);
			});
	}

	protected formatPricePerUser(monthlyPrice: number, yearlyPrice: number, currency?: string): string {
		const cur = currency || FeatureModuleSelectionBase.DEFAULT_CURRENCY;
		return `
				<label class="price-info-label">${messages["pricing.priceInfo.priceTotal"]}</label>
				<div>
					<span data-price="" class="data-text-large m-0">${formatDecimalNumber(this.isAnnualBillingSelected() ? yearlyPrice : monthlyPrice)}</span>
					<span class="price-info text-muted mr-15">${cur} ${messages["pricing.selectionDialog.perMonth." + this.getBillingCycleSelectionValue()]}</span>
					${!this.isAnnualBillingSelected() ? `<span class="price-info text-muted">(${messages["pricing.selectionDialog.annually"]} ${formatDecimalNumber(yearlyPrice)} ${cur})</span>` : ""}
				</div>
			`;
	}

	protected abstract toggleSaveButton(selectedModules: FeatureModule[]): void;

	protected isAnnualBillingSelected() {
		return this.billingCycleSelection?.getValue() === PaymentPeriod.ANNUALLY;
	}

	protected getBillingCycleSelectionValue() {
		return this.billingCycleSelection?.getValue() || PaymentPeriod.ANNUALLY;
	}
}
